import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { api, errorText } from '../api';
import type { Tarif } from '../billtoro/types';
import { fehlendeFunktion, grenzeAnbieten, upgradeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog, Dialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import {
  abosUrl,
  aboUrl,
  ersterTerminAb,
  imLauf,
  INTERVALL_KLEIN,
  leistungszeitraum,
  letzterTermin,
  terminPlus,
  zeitraumText,
  type Abo,
  type AboListe,
  type AboRechnung,
  type AboStatus,
  type JetztAntwort,
} from './AboDaten';
import { AboForm } from './AboForm';
import { PdfDialog } from './PdfDialog';
import { darf, fehlerText, GesperrtKnopf, grenzeFehlt, MitRecht, NurAnsehen, TarifMarke, tarifFehler } from './tarifHinweis';
import type { Overview } from './types';
import { VersandDialog, type VersandBeleg } from './VersandDialog';
import './abos.css';

const STATUS: Record<AboStatus, [string, string]> = {
  aktiv: ['Läuft', 'b-ok'],
  pausiert: ['Pausiert', 'b-none'],
  fehler: ['Angehalten', 'b-bad'],
  beendet: ['Beendet', 'b-none'],
};
const RE_STATUS: Record<AboRechnung['status'], string> = { offen: 'offen', teilweise: 'teilweise bezahlt', bezahlt: 'bezahlt', storniert: 'storniert' };
const anzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;
const pdfUrl = (id: string) => `/api/rechnungstool/rechnung/${id}/pdf`;
// Monat eines Tages in Worten („Dezember 2026“), für das Ende (letzter Leistungsmonat)
const monat = (d: string) => {
  const m = leistungszeitraum(d, 'monat', 'aktuell');
  return zeitraumText(m.von, m.bis);
};
type View = { url: string; title: string; beleg?: VersandBeleg };

// Wiederkehrende Rechnungen (Funktionsliste Punkt 8): /rechnung/wiederkehrend (Liste), /neu (auch ?rechnung=<id>:
// „Wiederkehrend machen“ an einer Rechnung), /:id (ändern)
export function Abos({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const [liste, setListe] = useState<AboListe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const firmId = data.firm.id;
  const aktuell = useRef(firmId);
  aktuell.current = firmId;

  const laden = useCallback(async () => {
    try {
      const res = await api.get<AboListe>(abosUrl(firmId));
      if (aktuell.current !== firmId) return;
      setListe(res);
      setError(null);
    } catch (err) {
      if (aktuell.current === firmId) setError(fehlerText(err));
    }
  }, [firmId]);

  useEffect(() => {
    setListe(null);
    void laden();
  }, [laden]);

  // nach einer Änderung: Liste und Übersicht (neue Rechnungen, neuer Kunde) neu
  const geaendert = useCallback(async () => {
    await Promise.all([laden(), reload()]);
  }, [laden, reload]);

  // Billtoro: Formulare nur mit dem Recht „Rechnungen“, sonst zurück zur Liste mit Hinweis
  const form = (
    <MitRecht tarif={data.tarif} bereich="rechnungen" zu="/rechnung/wiederkehrend">
      <AboForm data={data} onSaved={geaendert} />
    </MitRecht>
  );
  return (
    <Routes>
      <Route index element={<Liste liste={liste} error={error} laden={laden} geaendert={geaendert} tarif={data.tarif} />} />
      <Route path="neu" element={form} />
      <Route path=":id" element={form} />
      <Route path="*" element={<Navigate to="/rechnung/wiederkehrend" replace />} />
    </Routes>
  );
}

// „Neue wiederkehrende Rechnung“: Billtoro-Tarif ohne wiederkehrende Rechnungen oder schon alle angelegt: Marke und
// das Fenster „Mehr mit Billtoro“ statt des Formulars
function NeuKnopf({ tarif, children }: { tarif: Tarif | null | undefined; children: string }) {
  const fehlt = fehlendeFunktion(tarif, 'abos') ?? grenzeFehlt(tarif, 'abos');
  if (fehlt)
    return (
      <GesperrtKnopf
        tarif={tarif}
        fehlt={fehlt}
        className="btn primary"
        onUpgrade={() => upgradeAnbieten(tarif, 'abos') || grenzeAnbieten(tarif, 'abos')}
      >
        {children}
      </GesperrtKnopf>
    );
  return (
    <Link to="neu" className="btn primary">
      {children}
    </Link>
  );
}

function Liste({
  liste,
  error,
  laden,
  geaendert,
  tarif,
}: {
  liste: AboListe | null;
  error: string | null;
  laden(): Promise<void>;
  geaendert(): Promise<void>;
  tarif?: Tarif | null;
}) {
  const toast = useToast();
  const [jetzt, setJetzt] = useState<Abo | null>(null);
  const [weiter, setWeiter] = useState<Abo | null>(null);
  const [endeVon, setEndeVon] = useState<Abo | null>(null);
  const [loeschen, setLoeschen] = useState<Abo | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [send, setSend] = useState<VersandBeleg | null>(null);
  const [busy, setBusy] = useState(false);
  // Billtoro: ohne das Recht „Rechnungen“ nur ansehen (der Server prüft)
  const schreiben = darf(tarif, 'rechnungen');

  if (error && !liste) {
    return (
      <div className="notice bad rt-ku">
        <span>{error}</span>
        <button type="button" className="btn small" onClick={() => void laden()}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (!liste) return <div className="spinner" aria-label="Lädt" />;
  const { abos, heute, maxNachholen } = liste;
  const zahl = (s: AboStatus) => abos.filter((a) => a.status === s).length;
  const stand = [
    [zahl('aktiv'), 'läuft', 'laufen'],
    [zahl('fehler'), 'angehalten', 'angehalten'],
    [zahl('pausiert'), 'pausiert', 'pausiert'],
    [zahl('beendet'), 'beendet', 'beendet'],
  ] as const;

  // Aktion auf dem Server, danach die Liste neu (alles: auch die Übersicht, z. B. die Rechnungsliste)
  async function tun(f: () => Promise<unknown>, ok: string, alles = false) {
    setBusy(true);
    try {
      await f();
      await (alles ? geaendert() : laden());
      toast(ok);
      return true;
    } catch (err) {
      if (!tarifFehler(err)) toast(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const pause = (a: Abo) => void tun(() => api.post(aboUrl(a.id, '/pause')), `„${a.titel}“ pausiert`);
  // Weiter: ohne Fehler und ohne verpasste Termine gleich, sonst erst fragen (nachholen oder überspringen)
  function weiterKlick(a: Abo) {
    // Billtoro: wegen des Tarifs pausiert und der Tarif hat keine wiederkehrenden Rechnungen: gleich „Mehr mit Billtoro“
    if (a.pauseGrund === 'tarif' && upgradeAnbieten(tarif, 'abos')) return;
    if (!a.fehler && a.verpasst.length === 0) void tun(() => api.post(aboUrl(a.id, '/weiter'), { nachholen: true }), `„${a.titel}“ läuft wieder`);
    else setWeiter(a);
  }

  async function doJetzt() {
    if (!jetzt) return;
    const a = jetzt;
    setBusy(true);
    try {
      const r = await api.post<JetztAntwort>(aboUrl(a.id, '/jetzt'), { termin: a.naechste });
      setJetzt(null);
      await geaendert();
      const title = `Rechnung ${r.rechnung.number}`;
      if (r.versand?.status === 'fehler') toast(`${title} geschrieben, aber nicht verschickt: ${r.versand.fehler}`);
      else toast(`${title} geschrieben${r.versand ? ' und per E-Mail verschickt' : ''}`);
      setView({ url: pdfUrl(r.rechnung.id), title, beleg: { art: 'rechnung', id: r.rechnung.id, titel: title } });
    } catch (err) {
      setJetzt(null);
      if (!tarifFehler(err)) toast(errorText(err));
      await laden();
    } finally {
      setBusy(false);
    }
  }

  async function doLoeschen() {
    if (!loeschen) return;
    const a = loeschen;
    // alles neu: Die Rechnungen daraus haben danach kein Zeichen mehr in der Rechnungsliste
    await tun(() => api.del(aboUrl(a.id)), `„${a.titel}“ gelöscht`, true);
    setLoeschen(null);
  }

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>
            Wiederkehrende Rechnungen <NurAnsehen tarif={tarif} bereich="rechnungen" />
          </h1>
          <p className="muted">
            {abos.length === 0
              ? 'Noch keine'
              : stand
                  .filter(([n]) => n > 0)
                  .map(([n, eins, viele]) => `${n} ${n === 1 ? eins : viele}`)
                  .join(' · ')}
          </p>
        </div>
        {abos.length > 0 && schreiben && (
          <div className="rt-head-act">
            <NeuKnopf tarif={tarif}>+ Neue wiederkehrende Rechnung</NeuKnopf>
          </div>
        )}
      </div>

      {abos.length === 0 ? (
        <div className="rt-card rt-empty ab-leer">
          <p>
            Für feste Aufträge wie Wartung, Reinigung oder Miete: Die Rechnung entsteht jeden Monat, jedes Quartal, Halbjahr oder Jahr von
            selbst, mit dem Leistungszeitraum und auf Wunsch gleich per E-Mail.
          </p>
          {schreiben && (
            <>
              <NeuKnopf tarif={tarif}>Erste wiederkehrende Rechnung anlegen</NeuKnopf>
              <p className="ab-leer-tip">
                Oder in der <Link to="/rechnung/rechnungen">Rechnungsliste</Link> bei einer Rechnung „Wiederkehrend machen“ wählen.
              </p>
            </>
          )}
        </div>
      ) : (
        <ul className="ab-list">
          {abos.map((a) => (
            <AboKarte
              key={a.id}
              a={a}
              heute={heute}
              busy={busy}
              tarif={tarif}
              aendern={schreiben}
              onJetzt={() => setJetzt(a)}
              onPause={() => pause(a)}
              onWeiter={() => weiterKlick(a)}
              onEnde={() => setEndeVon(a)}
              onLoeschen={() => setLoeschen(a)}
              onErledigt={() => void tun(() => api.del(aboUrl(a.id, '/hinweis')), 'Hinweis erledigt')}
              onView={(r) => setView({ url: pdfUrl(r.id), title: `Rechnung ${r.number}`, beleg: { art: 'rechnung', id: r.id, titel: `Rechnung ${r.number}` } })}
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!jetzt}
        title="Rechnung jetzt schreiben?"
        text={jetzt ? jetztText(jetzt) : ''}
        confirmLabel="Ja, jetzt schreiben"
        busy={busy}
        onConfirm={() => void doJetzt()}
        onCancel={() => setJetzt(null)}
      />
      <WeiterDialog
        abo={weiter}
        heute={heute}
        max={maxNachholen}
        busy={busy}
        onClose={() => setWeiter(null)}
        onWeiter={async (nachholen) => {
          const a = weiter!;
          if (await tun(() => api.post(aboUrl(a.id, '/weiter'), { nachholen }), `„${a.titel}“ läuft wieder`)) setWeiter(null);
        }}
      />
      <EndeDialog
        abo={endeVon}
        busy={busy}
        onClose={() => setEndeVon(null)}
        onSave={async (ende) => {
          const a = endeVon!;
          const ok = await tun(
            () => api.put(aboUrl(a.id, '/ende'), { ende }),
            ende ? `„${a.titel}“ läuft bis ${monat(ende)}` : `„${a.titel}“ ohne Ende`,
          );
          if (ok) setEndeVon(null);
        }}
      />
      <ConfirmDialog
        open={!!loeschen}
        title={`„${loeschen?.titel ?? ''}“ löschen?`}
        text={
          loeschen
            ? `Es entstehen keine weiteren Rechnungen daraus. ${
                loeschen.rechnungen.length
                  ? `${loeschen.rechnungen.length === 1 ? 'Die geschriebene Rechnung bleibt' : `Die ${loeschen.rechnungen.length} geschriebenen Rechnungen bleiben`}. `
                  : ''
              }Zum Unterbrechen reicht „Pause“.`
            : ''
        }
        confirmLabel="Ja, löschen"
        danger
        busy={busy}
        onConfirm={() => void doLoeschen()}
        onCancel={() => setLoeschen(null)}
      />
      <PdfDialog
        url={view?.url ?? null}
        title={view?.title ?? ''}
        download={view ? `${view.url}?download=1` : undefined}
        onClose={() => setView(null)}
        onMail={
          view?.beleg && schreiben
            ? () => {
                setSend(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
      />
      <VersandDialog beleg={send} tarif={tarif} onClose={() => setSend(null)} onSent={geaendert} />
    </>
  );
}

function jetztText(a: Abo) {
  const danach = terminPlus(a.naechste, a.intervall, a.tag);
  return `Die Rechnung zum Termin ${fmtDate(a.naechste)}${a.leistung ? ` (Leistung ${zeitraumText(a.leistung.von, a.leistung.bis)})` : ''} bekommt die nächste Nummer und das Datum von heute. ${
    imLauf(a, danach) ? `Die nächste kommt dann am ${fmtDate(danach)}.` : 'Danach kommt keine mehr (Ende erreicht).'
  }${a.versand ? ' Sie geht gleich per E-Mail an den Kunden.' : ''}`;
}

function AboKarte({
  a,
  heute,
  busy,
  tarif,
  aendern,
  onJetzt,
  onPause,
  onWeiter,
  onEnde,
  onLoeschen,
  onErledigt,
  onView,
}: {
  a: Abo;
  heute: string;
  busy: boolean;
  tarif?: Tarif | null;
  // Billtoro: Recht „Rechnungen“ (ohne: keine Aktionen)
  aendern: boolean;
  onJetzt(): void;
  onPause(): void;
  onWeiter(): void;
  onEnde(): void;
  onLoeschen(): void;
  onErledigt(): void;
  onView(r: { id: string; number: string }): void;
}) {
  const [text, badge] = STATUS[a.status];
  const letzte = a.letzteRechnung ? (a.rechnungen.find((r) => r.id === a.letzteRechnung!.id) ?? null) : null;
  const beendet = a.status === 'beendet';
  const laeuft = a.aktiv && !a.fehler;
  const letzterAm = letzterTermin(a);
  // Billtoro: wegen des Tarifs pausiert; „Weiter“ trägt die Marke des Tarifs, der es erlaubt
  const tarifPause = a.pauseGrund === 'tarif';
  const aboFehlt = tarifPause ? (fehlendeFunktion(tarif, 'abos') ?? grenzeFehlt(tarif, 'abos')) : null;
  return (
    <li className={`ab-card ab-${a.status}`}>
      <div className="ab-top">
        <h2 className="ab-titel">{a.titel}</h2>
        <span className={`badge ${badge}`}>{text}</span>
      </div>
      <p className="ab-kunde-z">
        {a.kunde ? (
          <>
            {a.kunde.name}
            <span className="muted"> · Kunde {a.kunde.nummer}</span>
          </>
        ) : (
          a.kundeName || 'ohne Kunde'
        )}
      </p>
      <p className="ab-betrag">
        <b className="num">{eurC(a.brutto, a.waehrung)}</b> {INTERVALL_KLEIN[a.intervall]}
        {a.versand ? ' · per E-Mail' : ''}
      </p>
      <dl className="ab-daten">
        <div>
          <dt>Nächste Rechnung</dt>
          <dd>
            {beendet ? (
              'keine mehr'
            ) : (
              <>
                {fmtDate(a.naechste)}
                {a.leistung && <span className="muted"> für {zeitraumText(a.leistung.von, a.leistung.bis)}</span>}
                {laeuft && a.naechste <= heute && <span className="muted"> · entsteht in der nächsten Stunde</span>}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Letzte Rechnung</dt>
          <dd>
            {letzte ? (
              <>
                <button type="button" className="linkbtn" onClick={() => onView(letzte)}>
                  {letzte.number}
                </button>{' '}
                vom {fmtDate(letzte.invoiceDate)}
              </>
            ) : a.letzteRechnung ? (
              a.letzteRechnung.number
            ) : (
              <span className="muted">noch keine</span>
            )}
          </dd>
        </div>
        {a.ende && (
          <div>
            <dt>Läuft bis</dt>
            <dd>
              {monat(a.ende)}
              <span className="muted">{beendet ? ' · beendet' : letzterAm ? ` · letzte Rechnung am ${fmtDate(letzterAm)}` : ''}</span>
            </dd>
          </div>
        )}
      </dl>
      {a.fehler && (
        <div className="notice bad ab-note">
          <b>Angehalten:</b> {a.fehler}
        </div>
      )}
      {!a.fehler && a.hinweis && <div className="notice warn ab-note">{a.hinweis}</div>}
      {a.meldung && (
        <div className="notice warn ab-note ab-meldung">
          <span>{a.meldung}</span>
          {tarifPause ? (
            <Link className="btn small" to={`/rechnung/tarif${aboFehlt && aboFehlt !== 'bald' ? `?plan=${aboFehlt}` : ''}`}>
              Tarife ansehen
            </Link>
          ) : (
            aendern && (
              <button type="button" className="btn small" disabled={busy} onClick={onErledigt}>
                Erledigt
              </button>
            )
          )}
        </div>
      )}
      {!a.fehler && !laeuft && !beendet && a.verpasst.length > 0 && (
        <p className="ab-verpasst">
          Seit {fmtDate(a.verpasst[0]!)} {a.verpasst.length === 1 ? 'ist 1 Rechnung' : `sind ${a.verpasst.length} Rechnungen`} nicht entstanden.
        </p>
      )}
      {aendern && (
        <div className="ab-act">
          {!beendet && (
            <button type="button" className="linkbtn" disabled={busy} onClick={onJetzt}>
              Jetzt erzeugen
            </button>
          )}
          {laeuft ? (
            <button type="button" className="linkbtn" disabled={busy} onClick={onPause}>
              Pause
            </button>
          ) : (
            !beendet && (
              <button
                type="button"
                className={`linkbtn${a.fehler ? ' rt-warn' : ''}${aboFehlt ? ' rt-gesperrt' : ''}`}
                disabled={busy || aboFehlt === 'bald'}
                onClick={onWeiter}
              >
                Weiter
                <TarifMarke tarif={tarif} fehlt={aboFehlt} />
              </button>
            )
          )}
          <Link to={a.id} className="linkbtn">
            Ändern
          </Link>
          <button type="button" className="linkbtn" disabled={busy} onClick={onEnde}>
            {a.ende ? 'Ende ändern' : 'Ende'}
          </button>
          <button type="button" className="linkbtn rt-danger" disabled={busy} onClick={onLoeschen}>
            Löschen
          </button>
        </div>
      )}
      {a.rechnungen.length > 0 && (
        <details className="ab-re">
          <summary>{anzahl(a.rechnungen.length, 'Rechnung', 'Rechnungen')} daraus</summary>
          <ul>
            {a.rechnungen.map((r) => (
              <li key={r.id}>
                <button type="button" className="linkbtn num" onClick={() => onView(r)}>
                  {r.number}
                </button>
                <span className="muted">{fmtDate(r.invoiceDate)}</span>
                <span className="ab-re-z">{zeitraumText(r.serviceFrom, r.serviceTo ?? r.serviceFrom)}</span>
                <span className="num ab-re-b">{eurC(r.brutto, r.waehrung)}</span>
                <span className="muted ab-re-s">{RE_STATUS[r.status]}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

// Weiter nach Pause oder Fehler: verpasste Termine nachholen (höchstens max) oder ab heute weiter
function WeiterDialog({
  abo,
  heute,
  max,
  busy,
  onClose,
  onWeiter,
}: {
  abo: Abo | null;
  heute: string;
  max: number;
  busy: boolean;
  onClose(): void;
  onWeiter(nachholen: boolean): Promise<void>;
}) {
  const a = abo;
  const n = a?.verpasst.length ?? 0;
  const zuViele = n > max;
  const ab = a ? ersterTerminAb(a.naechste, a.intervall, a.tag, heute) : '';
  return (
    <Dialog open={!!a} onClose={onClose} title={a ? `„${a.titel}“ fortsetzen` : ''}>
      {a && (
        <>
          {a.fehler && (
            <>
              <div className="notice bad">{a.fehler}</div>
              <p className="muted">Bitte vorher die Ursache beheben (z. B. Kunde, Angaben oder E-Mail-Adresse), sonst hält sie gleich wieder an.</p>
            </>
          )}
          {n > 0 ? (
            <>
              <p>
                Seit {fmtDate(a.verpasst[0]!)} {zuViele ? `sind mehr als ${max} Rechnungen` : n === 1 ? 'ist 1 Rechnung' : `sind ${n} Rechnungen`} nicht
                entstanden{zuViele ? '' : ` (${a.verpasst.map(fmtDate).join(', ')})`}.
              </p>
              <p className="muted">
                {zuViele
                  ? `So viele holt das Programm nicht auf einmal nach. Es geht ab ${fmtDate(ab)} weiter; fehlende Rechnungen bitte von Hand schreiben.`
                  : `Nachholen: Die Rechnungen entstehen in der nächsten Stunde, jede für ihren Leistungszeitraum und mit dem Datum von heute. Überspringen: Es geht ab ${fmtDate(ab)} weiter.`}
              </p>
              <div className="dlg-actions">
                <button type="button" className="btn" onClick={onClose}>
                  Abbrechen
                </button>
                <button type="button" className={`btn${zuViele ? ' primary' : ''}`} disabled={busy} onClick={() => void onWeiter(false)}>
                  Ab {fmtDate(ab)} weiter
                </button>
                {!zuViele && (
                  <button type="button" className="btn primary" disabled={busy} onClick={() => void onWeiter(true)}>
                    {n === 1 ? 'Nachholen' : `${n} nachholen`}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="muted">
                Die nächste Rechnung entsteht am {fmtDate(a.naechste)}
                {a.naechste <= heute ? ' (in der nächsten Stunde)' : ''}.
              </p>
              <div className="dlg-actions">
                <button type="button" className="btn" onClick={onClose}>
                  Abbrechen
                </button>
                <button type="button" className="btn primary" disabled={busy} onClick={() => void onWeiter(true)}>
                  Weiter
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}

// Ende setzen oder entfernen: der letzte Leistungsmonat (keine Rechnung für einen Zeitraum, der danach beginnt)
function EndeDialog({ abo, busy, onClose, onSave }: { abo: Abo | null; busy: boolean; onClose(): void; onSave(ende: string | null): Promise<void> }) {
  const [ende, setEnde] = useState('');
  useEffect(() => setEnde(abo?.ende ?? ''), [abo]);
  const lz = abo ? leistungszeitraum(abo.naechste, abo.intervall, abo.zeitraum) : null;
  const letzte = abo && ende ? letzterTermin({ ...abo, ende }) : null;
  const letzteLz = abo && letzte ? leistungszeitraum(letzte, abo.intervall, abo.zeitraum) : null;
  return (
    <Dialog open={!!abo} onClose={onClose} title="Ende der wiederkehrenden Rechnung">
      {abo && lz && (
        <>
          <p className="muted">
            Bis zu welchem Monat die Leistung läuft, z. B. zum Vertragsende. Die nächste Rechnung wäre am {fmtDate(abo.naechste)} für{' '}
            {zeitraumText(lz.von, lz.bis)}.
          </p>
          <label className="field">
            <span>Läuft bis (letzter Leistungsmonat)</span>
            <input type="date" value={ende} onChange={(e) => setEnde(e.target.value)} />
          </label>
          {ende && !letzte && <p className="rt-warn-line">Damit entsteht keine Rechnung mehr.</p>}
          {letzte && letzteLz && (
            <p className="muted">
              Die letzte Rechnung ist die für {zeitraumText(letzteLz.von, letzteLz.bis)} am {fmtDate(letzte)}.
            </p>
          )}
          <div className="dlg-actions">
            {abo.ende && (
              <button type="button" className="btn ab-ende-weg" disabled={busy} onClick={() => void onSave(null)}>
                Ende entfernen
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" disabled={busy || !ende} onClick={() => void onSave(ende)}>
              Speichern
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
